import React from 'react';

import { useIntl } from 'react-intl';

import Image from '@atlaskit/image';
import { Inline, Text } from '@atlaskit/primitives/compiled';

import { LikeIcon } from '../../constants/icons/like';

import { messages } from './messages';
import {
	resolveStandardSlackEmojiUnicode,
	toBareEmojiName,
} from './resolve-standard-slack-emoji-unicode';

const GIF_URL_PATTERN = /\.gif(?:$|[?#])/i;

const isLikelyAnimatedEmojiUrl = (url: string): boolean => GIF_URL_PATTERN.test(url);

const FrozenReactionImage = ({
	src,
	alt,
	width,
	height,
	testId,
	onError,
}: {
	src: string;
	alt: string;
	width: number;
	height: number;
	testId: string;
	onError: () => void;
}): React.JSX.Element | null => {
	const [hasCanvasError, setHasCanvasError] = React.useState(false);
	const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

	React.useEffect(() => {
		if (hasCanvasError) {
			onError();
		}
	}, [hasCanvasError, onError]);

	React.useEffect(() => {
		setHasCanvasError(false);
	}, [src]);

	React.useEffect(() => {
		if (hasCanvasError) {
			return;
		}

		const image = new window.Image();

		image.onload = () => {
			const canvas = canvasRef.current;
			if (!canvas) {
				setHasCanvasError(true);
				return;
			}

			const context = canvas.getContext('2d');
			if (!context) {
				setHasCanvasError(true);
				return;
			}

			const drawWidth = image.naturalWidth || image.width;
			const drawHeight = image.naturalHeight || image.height;
			if (!drawWidth || !drawHeight) {
				setHasCanvasError(true);
				return;
			}

			try {
				context.clearRect(0, 0, width, height);
				context.drawImage(image, 0, 0, drawWidth, drawHeight, 0, 0, width, height);
			} catch {
				setHasCanvasError(true);
			}
		};

		image.onerror = () => setHasCanvasError(true);
		image.src = src;
	}, [src, width, height, hasCanvasError]);

	if (hasCanvasError) {
		return null;
	}

	return (
		<span role="img" aria-label={alt} data-testid={testId}>
			<canvas ref={canvasRef} width={width} height={height} />
		</span>
	);
};

export const TopReaction = ({
	emojiUrl,
	emojiName,
	count,
	cloudId,
	baseUrl,
}: {
	emojiUrl?: string | null;
	emojiName?: string | null;
	count: number;
	/** Passed through to `getEmojiResource` — same as lazy `Emoji` in `common/ui/emoji/main.tsx`. */
	cloudId?: string;
	baseUrl?: string;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [hasImageError, setHasImageError] = React.useState(false);
	const [unicodeEmoji, setUnicodeEmoji] = React.useState<string | undefined>(undefined);
	const shouldRenderFrozenGif = Boolean(emojiUrl && isLikelyAnimatedEmojiUrl(emojiUrl));
	const fallbackImageLabel = formatMessage(messages.imageReactionFallbackLabel);
	const reactionImageAlt = emojiName || fallbackImageLabel;
	const handleReactionImageError = React.useCallback(() => {
		setHasImageError(true);
	}, []);

	const shouldRenderEmojiImage = !!emojiUrl && !hasImageError;
	const shouldResolveUnicode = !shouldRenderEmojiImage && Boolean(emojiName);

	React.useEffect(() => {
		setHasImageError(false);
	}, [emojiUrl]);

	React.useEffect(() => {
		if (!shouldResolveUnicode) {
			setUnicodeEmoji(undefined);
			return;
		}

		let cancelled = false;

		void resolveStandardSlackEmojiUnicode(emojiName, cloudId, baseUrl).then((resolvedUnicode) => {
			if (!cancelled) {
				setUnicodeEmoji(resolvedUnicode);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [shouldResolveUnicode, emojiName, cloudId, baseUrl]);

	const reactionVisual = (() => {
		if (shouldRenderEmojiImage) {
			if (shouldRenderFrozenGif && emojiUrl) {
				return (
					<FrozenReactionImage
						src={emojiUrl}
						alt={reactionImageAlt}
						width={16}
						height={16}
						testId="top-reaction-emoji-frozen"
						onError={handleReactionImageError}
					/>
				);
			}

			return (
				<Image
					src={emojiUrl}
					alt={reactionImageAlt}
					width={16}
					height={16}
					testId="top-reaction-emoji"
					onError={handleReactionImageError}
				/>
			);
		}

		if (unicodeEmoji && emojiName) {
			return (
				<span
					role="img"
					aria-label={formatMessage(messages.unicodeReactionAriaLabel, {
						emojiName: toBareEmojiName(emojiName),
					})}
					data-testid="top-reaction-emoji-unicode"
				>
					<Text size="small">{unicodeEmoji}</Text>
				</span>
			);
		}

		return <LikeIcon />;
	})();

	return (
		<Inline space="space.050" alignBlock="center">
			{reactionVisual}
			<Text size="small" color="color.text.subtlest">
				{count}
			</Text>
		</Inline>
	);
};

export const getTopReaction = ({
	emojiUrl,
	emojiName,
	count,
	cloudId,
	baseUrl,
}: {
	emojiUrl?: string | null;
	emojiName?: string | null;
	count?: number | null;
	cloudId?: string;
	baseUrl?: string;
}): React.JSX.Element | null => {
	if (!count) {
		return null;
	}

	return (
		<TopReaction
			emojiUrl={emojiUrl}
			emojiName={emojiName}
			count={count}
			cloudId={cloudId}
			baseUrl={baseUrl}
		/>
	);
};
